import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Menu, X, LogOut } from "lucide-react";

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const links = [
    { to: "/", label: "Home" },
    { to: "/the-nation", label: "The Nation" },
    { to: "/census", label: "The Black Census" },
    { to: "/legal-standing", label: "Legal Standing" },
    { to: "/cerd-obligations", label: "CERD" },
    { to: "/political-engagement", label: "Political Engagement" },
    { to: "/library", label: "Publications" },
    { to: "/committees", label: "Committees" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-[#D4AF37]/25">
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-12 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link">
          <img src={ASSETS.seal} alt="iBAG Seal" className="h-12 w-12 object-contain" />
          <div className="leading-tight whitespace-nowrap xl:hidden 2xl:block">
            <div className="font-serif-display text-lg font-semibold tracking-tight text-[#F5F3EF]">The Black American Government</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Indigenous Black American Nation</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-sm font-medium text-[#F5F3EF] hover:text-[#D4AF37] transition-colors whitespace-nowrap"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
          {user && user.role === "admin" && (
            <Link to="/admin" className="text-sm font-medium text-[#F5F3EF] hover:text-[#D4AF37] transition-colors" data-testid="nav-link-admin">
              Admin
            </Link>
          )}
          {user ? (
            <div className="flex items-center gap-4">
              <Link to="/dashboard" data-testid="nav-link-dashboard">
                <Button variant="outline" className="rounded-none bg-transparent border-[#D4AF37]/50 text-[#F5F3EF] hover:bg-[#D4AF37] hover:text-[#0A0A0A]">Dashboard</Button>
              </Link>
              <button onClick={handleLogout} className="text-[#A8A29E] hover:text-[#C8102E] transition-colors" data-testid="nav-logout-btn" title="Logout">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/login" data-testid="nav-login-link">
                <Button variant="ghost" className="rounded-none text-[#F5F3EF] hover:text-[#D4AF37] hover:bg-transparent">Member Login</Button>
              </Link>
              <Link to="/register" data-testid="nav-register-link">
                <Button className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white">Become a Member</Button>
              </Link>
            </div>
          )}
        </nav>

        <button className="xl:hidden text-[#F5F3EF]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-[#0A0A0A] border-t border-[#D4AF37]/25 px-6 py-4 flex flex-col gap-4">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-sm font-medium text-[#F5F3EF]" data-testid={`nav-mobile-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
          {user && user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-sm font-medium text-[#F5F3EF]">Admin</Link>}
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm font-medium text-[#F5F3EF]">Dashboard</Link>
              <button onClick={handleLogout} className="text-sm text-left text-[#C8102E]">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-medium text-[#F5F3EF]">Member Login</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="text-sm font-medium text-[#D4AF37]">Become a Member</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
